window.o_global = window.o_global || {};
window.o_util = window.o_util || {};

/**
 * Utility collection of animation functions.
 *
 * @return {Object} Public methods of o_util.animation.
 */
o_util.animation = (function() {
    "use strict";

    /**
     * Various easing functions for animations over time.
     * More: https://gist.github.com/gre/1650294
     *
     * @example
     * o_util.animation.easings.linear(0.5) // => 0.5
     * o_util.animation.easings.easeOutCubic(0.5) // => 0.875
     *
     * @param {Number} t    The current status of the animation in the interval [0,1]. E.g. 0.5 means that half of the animation time is finished.
     *
     * @return {Number}     The animation status depending on the time status.
     */
    var easings = Object.freeze({
        "linear": function(t) {
            return t;
        },
        "easeOutCubic": function(t) {
            return (--t) * t * t + 1;
        }
    });

    /**
     * Various transition classes for predefined
     * animations like fadeOut, fadeIn that can be passed
     * to the o_util.animation.transition function.
     *
     * @see https://*.otto.de/assets-asset-pali/animationen.html#animations-eigenschaften
     *
     * @example
     * o_util.animation.transition(el, o_util.animation.transitions.fadeOut);
     * o_util.animation.transition(el, o_util.animation.transitions.fadeIn);
     *
     * @return (String) A transition animation class string (without leading dot).
     */
    var transitions = Object.freeze({
        "fadeOut": "p_animation__fadeOut",
        "fadeIn": "p_animation__fadeIn"
    });

    /**
     * Adds a CSS class with a transition animation to an element and triggers
     * a callback when the specified transition finished/ended.
     *
     * IMPORTANT: Only use this function if you need the callback! Otherwise simply
     * add the class to your element. The callback will only be invoked
     * if your CSS class triggers a transition animation.
     *
     * There will be no animation for browsers not supporting CSS3 animations (e.g. IE9), but the callback still fires.
     *
     * @example
     * // Trigger a fadeOut animation
     * o_util.animation.transition(el, o_util.animation.transitions.fadeOut, myCallbackHandler);
     * // Add your own transition class
     * o_util.animation.transition(el, "p_awesome__transition--animation", myCallbackHandler);
     *
     * @param {HTMLElement}     target                  The HTML element to perform the transition on.
     * @param {String}          transitionClass         A transition animation class string (without leading dot)
     * @param {Function}        callback                Invoked callback after transition finished.
     */
    function transition(target, transitionClass, callback) {
        if (typeof target === "object" && typeof transitionClass === "string") {
            var transitionEndEvent = o_util.event.whichTransitionEndEvent(),
                callbackHandler = function(event) {
                    typeof callback === "function" ? callback() : null;

                    if (event && event.target) {
                        event.target.removeEventListener(transitionEndEvent, callbackHandler);
                    }
                };

            if (transitionEndEvent && typeof callback === "function" && !target.classList.contains(transitionClass)) {
                target.addEventListener(transitionEndEvent, callbackHandler);
                target.classList.add(transitionClass);
            } else {
                target.classList.add(transitionClass);
                callbackHandler();

            }
        } else {
            throw new Error("No target or class specified");
        }
    }

    /**
     * This is a shorthand for o_util.animation.transition when
     * used with the global pattern default class `p_animation__fadeOut`
     * (o_util.animation.transitions.fadeOut).
     * So instead of the long term
     *      o_util.animation.transition(el, o_util.animation.transitions.fadeOut, myCallbackHandler)
     * you can use
     *      o_util.animation.fadeOut(el, myCallbackHandler)
     *
     * The transition is performed on the `opacity` attribute (-> `0`
     * ).
     * If the computed opacity is already `0` the callback gets invoked directly.
     *
     * IMPORTANT: Only use this function if you need the callback! Otherwise simply
     * add the class to your element. Make sure to remove the fading class when you
     * fadeIn and fadeOut again.
     *
     * @param {HTMLElement} target      The HTML element to perform the transition on.
     * @param {Function}    callback    Invoked callback after element faded out.
     */
    function fadeOut(target, callback) {
        if (window.getComputedStyle(target).opacity !== "0") {
            o_util.animation.transition(target, transitions.fadeOut, callback);
        } else if (typeof callback === "function") {
            callback();
        }
    }

    /**
     * This is a shorthand for o_util.animation.transition when
     * used with the global pattern default class `p_animation__fadeIn`
     * (o_util.animation.transitions.fadeIn).
     * So instead of the long term
     *      o_util.animation.transition(el, o_util.animation.transitions.fadeIn, myCallbackHandler)
     * you can use
     *      o_util.animation.fadeIn(el, myCallbackHandler)
     *
     * The transition is performed on the `opacity` attribute (-> `1`).
     * If the computed opacity is already `1` the callback gets invoked directly.
     *
     * IMPORTANT: Only use this function if you need the callback! Otherwise simply
     * add the class to your element. Make sure to remove the fading class when you
     * fadeOut and fadeIn again.
     *
     * @param {HTMLElement} target      The HTML element to perform the transition on.
     * @param {Function}    callback    Invoked callback after element faded in.
     */
    function fadeIn(target, callback) {
        if (window.getComputedStyle(target).opacity !== "1") {
            o_util.animation.transition(target, transitions.fadeIn, callback);
        } else if (typeof callback === "function") {
            callback();
        }
    }

    /**
     * Shim for requestAnimationFrame.
     *
     * You can use this like the normal requestAnimation
     * You can use this like the normal window.requestAnimationFrame.
     * Just type in o_global.helper.requestAnimFrame(func).
     */
    function requestAnimFrame() {
        var fn = window.requestAnimationFrame ||
            window.webkitRequestAnimationFrame ||
            window.mozRequestAnimationFrame ||
            function(callback) {
                window.setTimeout(callback, 1000 / 60);
            };

        fn.apply(null, [].slice.call(arguments));
    }

    /**
     * Scrolls the page to the element or a position within a specific duration.
     *
     * @example
     * // Scrolls the page to position 2000 within one second.
     * o_global.helper.scrollTo(2000, 1000);
     * // Scrolls to the top of the page within two seconds.
     * o_global.helper.scrollTo(0, 2000);
     * // Scrolls to elem within a half second.
     * o_global.helper.scrollTo(elem, 500);
     * // Scrolls to elem within three seconds with a linear scrolling animation instead of the default easeOutCubic.
     * o_global.helper.scrollTo(elem, 3000, "linear");
     *
     * @param {Object|Number} target          The HTML Element or the y-position of the page, where the page should be scrolled to.
     * @param {Number}        duration        The duration of the animation in milliseconds.
     * @param {String}        [easing]        Optional argument for defining the easing function for the scrolling animation. Available functions in o_global.helper.Easings - Default is: "easeOutCubic"
     */
    function scrollTo(target, duration, easing) {
        var position, scrollBase, currentPos, easingFnc, maxScroll, windowTop, ratio;

        windowTop = typeof window.scrollY === "undefined" ? window.pageYOffset : window.scrollY;

        if (typeof target === "object" && !!target.getBoundingClientRect) {
            position = target.getBoundingClientRect().top + windowTop - 20;
        } else if (typeof target === "number") {
            position = target;
        } else {
            throw new Error("Invalid target for scrollTo() method. Element has to be an object with an offsetTop function or number, which stands for the page position.");
        }

        // Select the easing function, which should be applied on the scroll animation.
        easing = easing || "easeOutCubic";
        easingFnc = o_util.core.convertStringToFunction("o_util.animation.easings." + easing, window);

        if (!easingFnc) {
            throw new Error("Unknown easing function for scrollTo() method: " + easing);
        }

        // Validate the correct scroll container. It depends on the browser, if the html or the body scrolls.
        scrollBase = document.documentElement;
        currentPos = scrollBase.scrollTop;

        if (currentPos === 0) {
            ratio = !!window.devicePixelRatio ? window.devicePixelRatio : 1;
            scrollBase.scrollTop = scrollBase.scrollTop + Math.max(1, ratio, (1 / ratio));
            scrollBase = scrollBase.scrollTop > 0 ? scrollBase : document.body;
            scrollBase.scrollTop = 0;
        }

        // You cannot scroll further than the page height.
        maxScroll = scrollBase.scrollHeight - scrollBase.offsetHeight;
        if (maxScroll < position) {
            position = maxScroll;
        }

        // No animation for negative, zero and values lower than 16 milliseconds, because within this duration there would be only one animation frame.
        if (duration < 16) {
            scrollBase.scrollTop = position;
            return;
        }

        _scrollTo(scrollBase, currentPos, position, 0, duration, easingFnc);
    }

    /**
     * Will be called multiple times until the scroll animation is finished.
     * After update the scroll position to the next animation step, it registers a timeout with this function again for the next animation step.
     *
     * @private
     *
     * @param {Object} scrollBase   Depending on the used browser, the html container which scrolls the page (e.g. body or html).
     * @param {Number} scrollFrom   The y-position of the scroll-start in pixel.
     * @param {Number} scrollTo     The y-position of the scroll-end in pixel.
     * @param {Number} startTime    The start time of the scrolling animation
     * @param {Number} duration     The percent value, which will be add to the process after each scroll step. Must be greater 0!
     * @param {Function} easingFnc  Easing function for the scrolling animation.
     */
    function _scrollTo(scrollBase, scrollFrom, scrollTo, startTime, duration, easingFnc) {
        var current = new Date().getTime(),
            currentState;

        // Calculate the current status (in percent - interval [0,1]) of the animation.
        startTime = startTime === 0 ? current : startTime;
        currentState = (current - startTime) / duration;

        // Abort if animation is finished.
        if (currentState > 1) {
            scrollBase.scrollTop = scrollTo;
            return;
        }

        // Update scroll position and current process of the whole animation.
        scrollBase.scrollTop = _getNewScrollPosition(scrollFrom, scrollTo, currentState, easingFnc);

        // Register timeout for next animation step.
        requestAnimFrame(function() {
            _scrollTo(scrollBase, scrollFrom, scrollTo, startTime, duration, easingFnc);
        });
    }

    /**
     * Caculate the new scroll position of the scolling animation.
     *
     * @private
     *
     * @param {Number} from         The start position of the scrolling.
     * @param {Number} to           The end position of the scrolling.
     * @param {Number} currentState The current state of the animation. Interval [0,1]
     * @param {Function} easingFnc  The easing function.
     *
     * @return {Number}             The current scroll position.
     */
    function _getNewScrollPosition(from, to, currentState, easingFnc) {
        return from - (from - to) * easingFnc(currentState);
    }

    /**
     * Return public API.
     */
    return {
        /* TEST-CODE */
        "_scrollTo": _scrollTo,
        "_getNewScrollPosition": _getNewScrollPosition,
        /* END TEST-CODE */
        "easings": easings,
        "transitions": transitions,
        "requestAnimFrame": requestAnimFrame,
        "scrollTo": scrollTo,
        "transition": transition,
        "fadeOut": fadeOut,
        "fadeIn": fadeIn
    };
}());

// Namespace fallbacks.
o_global.helper = o_global.helper || {};
o_global.helper.Easings = o_util.animation.easings;
o_global.helper.requestAnimFrame = o_util.animation.requestAnimFrame;
o_global.helper._getNewScrollPosition = o_util.animation._getNewScrollPosition;
o_global.helper.scrollTo = o_util.animation.scrollTo;
